import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import widgetSettingsService, { WidgetSettings, WidgetTheme } from '../../services/widgetSettingsService';
import resourceService from '../../services/resourceService';
import ColorPicker from './ColorPicker';
import AvatarUploader from './AvatarUploader';
import WidgetPreview from './WidgetPreview';
import TwoFactorSetup from './TwoFactorSetup';

const WidgetSettingsComponent: React.FC = () => {
  const [settings, setSettings] = useState<WidgetSettings>({});
  const [themes, setThemes] = useState<WidgetTheme[]>([]);
  const [resources, setResources] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  const [hasChanges, setHasChanges] = useState(false);

  // Load initial data
  useEffect(() => {
    loadSettings();
    loadThemes();
    loadResources();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await widgetSettingsService.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load widget settings');
    } finally {
      setIsLoading(false);
    }
  };

  const loadThemes = async () => {
    try {
      const data = await widgetSettingsService.getThemes();
      setThemes(data);
    } catch (error) {
      console.error('Error loading themes:', error);
    }
  };

  const loadResources = async () => {
    try {
      const data = await resourceService.getResources();
      setResources(data);
    } catch (error) {
      console.error('Error loading resources:', error);
    }
  };

  const handleSettingChange = (key: keyof WidgetSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updatedSettings = await widgetSettingsService.updateSettings(settings);
      setSettings(updatedSettings);
      setHasChanges(false);
      toast.success('Widget settings saved successfully!');
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageUpload = async (file: File, type: 'avatar' | 'logo') => {
    try {
      const response = await widgetSettingsService.uploadImage(file, type);
      
      // Update settings with new image URL
      const urlKey = type === 'avatar' ? 'avatar_url' : 'logo_url';
      const filenameKey = type === 'avatar' ? 'avatar_filename' : 'logo_filename';
      
      setSettings(prev => ({
        ...prev,
        [urlKey]: response.imageUrl,
        [filenameKey]: response.filename
      }));
      
      setHasChanges(true);
      toast.success(`${type} uploaded successfully!`);
      
      return response;
    } catch (error: any) {
      toast.error(error.message || `Failed to upload ${type}`);
      throw error;
    }
  };

  const handleImageDelete = async (type: 'avatar' | 'logo') => {
    try {
      await widgetSettingsService.deleteImage(type);
      
      // Update settings to remove image
      const urlKey = type === 'avatar' ? 'avatar_url' : 'logo_url';
      const filenameKey = type === 'avatar' ? 'avatar_filename' : 'logo_filename';
      
      setSettings(prev => ({
        ...prev,
        [urlKey]: null,
        [filenameKey]: null
      }));
      
      setHasChanges(true);
      toast.success(`${type} deleted successfully!`);
    } catch (error: any) {
      toast.error(error.message || `Failed to delete ${type}`);
      throw error;
    }
  };

  const handleApplyTheme = async (themeId: number) => {
    try {
      await widgetSettingsService.applyTheme(themeId);
      await loadSettings(); // Reload to get updated colors
      toast.success('Theme applied successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to apply theme');
    }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults? This will keep your uploaded images but reset all other settings.')) {
      return;
    }

    try {
      const defaultSettings = await widgetSettingsService.resetToDefaults();
      setSettings(defaultSettings);
      setHasChanges(false);
      toast.success('Settings reset to defaults!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset settings');
    }
  };

  const tabs = [
    { id: 'branding', label: 'School Branding', icon: '🏫' },
    { id: 'chatbot', label: 'Chatbot Personality', icon: '🤖' },
    { id: 'appearance', label: 'Visual Design', icon: '🎨' },
    { id: 'behavior', label: 'Behavior', icon: '⚙️' },
    { id: 'themes', label: 'Themes', icon: '✨' },
    { id: 'security', label: 'Account Security', icon: '🔐' }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Widget Settings</h2>
          <p className="text-gray-600">Customize your chat widget appearance and behavior</p>
        </div>
        
        <div className="flex items-center space-x-3">
          {hasChanges && (
            <span className="text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
              Unsaved changes
            </span>
          )}
          
          <button
            onClick={handleResetToDefaults}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Reset to Defaults
          </button>
          
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            {activeTab === 'branding' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">School Branding</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      School Name *
                    </label>
                    <input
                      type="text"
                      value={settings.school_name || ''}
                      onChange={(e) => handleSettingChange('school_name', e.target.value)}
                      placeholder="My Academy"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This will appear in the widget header and welcome messages
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Widget Position
                    </label>
                    <select
                      value={settings.position || 'bottom-right'}
                      onChange={(e) => handleSettingChange('position', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="bottom-right">Bottom Right</option>
                      <option value="bottom-left">Bottom Left</option>
                      <option value="top-right">Top Right</option>
                      <option value="top-left">Top Left</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unique Selling Proposition
                  </label>
                  <textarea
                    value={settings.unique_selling_proposition || ''}
                    onChange={(e) => handleSettingChange('unique_selling_proposition', e.target.value)}
                    placeholder="Personalized education for every child's unique needs"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    A brief description of what makes your school special
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Call-to-Action Text
                  </label>
                  <input
                    type="text"
                    value={settings.call_to_action_text || ''}
                    onChange={(e) => handleSettingChange('call_to_action_text', e.target.value)}
                    placeholder="Got questions? Click here"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Text that appears next to the chat button
                  </p>
                  <p className="mt-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    💡 This text also appears in the auto-show message bubble (see Behavior tab)
                  </p>
                </div>

                {/* Newsletter Resource Selection */}
                <div className="border-t pt-6 mt-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Newsletter Lead Magnet</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Newsletter Resource
                    </label>
                    <select
                      value={settings.newsletter_resource_id || ''}
                      onChange={(e) => handleSettingChange('newsletter_resource_id', e.target.value ? Number(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No resource selected</option>
                      {resources.map((resource) => (
                        <option key={resource.id} value={resource.id}>
                          {resource.title}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      This resource will be automatically delivered to newsletter signups via /newsletter form
                    </p>
                    {settings.newsletter_resource_id && (
                      <p className="mt-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                        ✓ Resource configured - newsletter signups will receive an instant download link
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'chatbot' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Chatbot Personality</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chatbot Name *
                  </label>
                  <input
                    type="text"
                    value={settings.chatbot_name || ''}
                    onChange={(e) => handleSettingChange('chatbot_name', e.target.value)}
                    placeholder="School Assistant"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    The name visitors will see for your chatbot
                  </p>
                </div>

                <AvatarUploader
                  type="avatar"
                  currentImageUrl={settings.avatar_url}
                  onUpload={(file) => handleImageUpload(file, 'avatar')}
                  onDelete={() => handleImageDelete('avatar')}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chatbot Personality
                  </label>
                  <textarea
                    value={settings.chatbot_personality || ''}
                    onChange={(e) => handleSettingChange('chatbot_personality', e.target.value)}
                    placeholder="Friendly and knowledgeable educational consultant"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Describe how your chatbot should behave and communicate
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Welcome Message
                  </label>
                  <textarea
                    value={settings.welcome_message || ''}
                    onChange={(e) => handleSettingChange('welcome_message', e.target.value)}
                    placeholder="Hi! Welcome to {{school_name}}. I help families find the right educational support for their children. What brings you here today?"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    First message visitors see when they start a conversation. Use <code>{'{school_name}'}</code> and <code>{'{chatbot_name}'}</code> for dynamic content.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Visual Design</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ColorPicker
                    label="Primary Color"
                    value={settings.primary_color || '#4F46E5'}
                    onChange={(color) => handleSettingChange('primary_color', color)}
                    description="Main brand color for buttons and headers"
                  />
                  
                  <ColorPicker
                    label="Secondary Color"
                    value={settings.secondary_color || '#7C3AED'}
                    onChange={(color) => handleSettingChange('secondary_color', color)}
                    description="Secondary color for gradients and accents"
                  />
                  
                  <ColorPicker
                    label="Accent Color"
                    value={settings.accent_color || '#10B981'}
                    onChange={(color) => handleSettingChange('accent_color', color)}
                    description="Color for success messages and highlights"
                  />
                  
                  <ColorPicker
                    label="Text Color"
                    value={settings.text_color || '#1F2937'}
                    onChange={(color) => handleSettingChange('text_color', color)}
                    description="Primary text color"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Border Radius
                    </label>
                    <select
                      value={settings.border_radius || '12px'}
                      onChange={(e) => handleSettingChange('border_radius', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="4px">Small (4px)</option>
                      <option value="8px">Medium (8px)</option>
                      <option value="12px">Large (12px)</option>
                      <option value="16px">Extra Large (16px)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}


            {activeTab === 'behavior' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Widget Behavior</h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Auto-Show Message Bubble</label>
                      <p className="text-xs text-gray-500">Show your call-to-action message bubble after page load (non-intrusive)</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.auto_open_enabled || false}
                      onChange={(e) => handleSettingChange('auto_open_enabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>

                  {settings.auto_open_enabled && (
                    <>
                      <div className="ml-6 border-l-2 border-blue-200 pl-4 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Bubble Show Delay
                          </label>
                          <select
                            value={settings.auto_open_delay || 5}
                            onChange={(e) => handleSettingChange('auto_open_delay', parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value={3}>3 seconds</option>
                            <option value={5}>5 seconds</option>
                            <option value={10}>10 seconds</option>
                            <option value={15}>15 seconds</option>
                            <option value={30}>30 seconds</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-500">
                            How long to wait before showing the message bubble
                          </p>
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">Show to New Visitors Only</label>
                            <p className="text-xs text-gray-500">Only show message bubble to first-time visitors</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.auto_open_only_new_visitors ?? true}
                            onChange={(e) => handleSettingChange('auto_open_only_new_visitors', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Show Branding</label>
                      <p className="text-xs text-gray-500">Display school branding in the widget</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.show_branding || false}
                      onChange={(e) => handleSettingChange('show_branding', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Typing Indicator</label>
                      <p className="text-xs text-gray-500">Show typing indicator when chatbot is responding</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.enable_typing_indicator || false}
                      onChange={(e) => handleSettingChange('enable_typing_indicator', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Enable Analytics</label>
                      <p className="text-xs text-gray-500">Track widget usage and conversation metrics</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.enable_analytics || false}
                      onChange={(e) => handleSettingChange('enable_analytics', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'themes' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Predefined Themes</h3>
                <p className="text-sm text-gray-600">
                  Choose from our professionally designed color themes or customize your own colors in the Appearance tab.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {themes.map((theme) => (
                    <div
                      key={theme.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{theme.name}</h4>
                        {theme.is_default && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            Default
                          </span>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-3">{theme.description}</p>
                      
                      <div className="flex items-center space-x-2 mb-3">
                        <div 
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: theme.color_scheme.primary }}
                          title="Primary"
                        />
                        <div 
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: theme.color_scheme.secondary }}
                          title="Secondary"
                        />
                        <div 
                          className="w-6 h-6 rounded border border-gray-300"
                          style={{ backgroundColor: theme.color_scheme.accent }}
                          title="Accent"
                        />
                      </div>
                      
                      <button
                        onClick={() => handleApplyTheme(theme.id)}
                        className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                      >
                        Apply Theme
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div>
                <TwoFactorSetup />
              </div>
            )}
          </div>
        </div>

        {/* Preview Panel - Hide for security tab */}
        {activeTab !== 'security' && (
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <WidgetPreview settings={settings} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WidgetSettingsComponent;